import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import {
  CarePlanPickerGoal,
  CarePlanPickerPlan,
  CarePlanPickerStatus,
  CarePlanWorkRequestItem,
  CarePlanWorkRequestPlan,
} from 'src/app/models/ccm/care-plan-work.model';
import {
  CARE_PLAN_WORK_LIMITS,
  sortRequestPlans,
  validateCarePlanWork,
} from 'src/app/services/ccm/care-plan-work.utils';

/** Interventions listed before "Show all N". */
export const COLLAPSED_INTERVENTIONS = 3;

type PickBox = 'goal' | 'interventions';

/** Stands in for the form callbacks until Angular registers them. */
const noop = (): void => undefined;

/** Gives each picker on a page its own heading id: the time form can hold several CCM rows. */
let nextPickerId = 0;

/**
 * The care plan items behind one CCM time row (BRD-CLIN-2026-008 FR-3): every Active care plan of the
 * patient as its own section, two boxes per goal — the goal, and its interventions as one item — and a
 * required one-line note for each plan picked from.
 *
 * A form control: its value is the `care_plan_work` the time form sends for the row, and its validity —
 * `validateCarePlanWork`, plus "not loaded" while `status` is not ready — keeps Save off through the
 * form's own `invalid` (FR-3.4). The value is emitted only when the picks really change, so loading the
 * sections never marks the time form dirty.
 *
 * A box can always be unticked. A goal from the frozen copy of an entry being edited can only get back
 * the boxes it was saved with: the server refuses a new pick on a plan or goal that is gone.
 */
@Component({
  selector: 'app-ccm-care-plan-picker',
  templateUrl: './ccm-care-plan-picker.component.html',
  styleUrls: ['./ccm-care-plan-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CcmCarePlanPickerComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => CcmCarePlanPickerComponent), multi: true },
  ],
})
export class CcmCarePlanPickerComponent implements ControlValueAccessor, Validator, OnChanges {
  /**
   * False for an entry already CCM before go live with no saved picks, which the server lets be edited
   * without care plan work (FR-5.3). Anything picked still needs its note.
   *
   * Not named `required`: a `[required]` binding next to `[formControl]` would also attach Angular's
   * `RequiredValidator`, which reads an empty array as missing and makes "nothing picked" invalid always.
   */
  @Input() requirePicks = true;

  /** Until the sections are loaded the picker says so and keeps its row invalid. */
  @Input() status: CarePlanPickerStatus = 'ready';

  /** Which row this is, e.g. "Time 2", read out with the heading when the time form holds several CCM rows. */
  @Input() rowLabel = '';

  /**
   * Asked for from the "Try again" button when the sections failed to load. Closing the time form instead
   * would drop the timer's minutes.
   */
  @Output() retry = new EventEmitter<void>();

  /**
   * The time form can take the nurse to the Care Plans tab and back with the entry kept (S2.4, FR-4.1):
   * not when the form sits in the timer's leave modal, which has no way back to it.
   */
  @Input() canCreateCarePlan = false;

  /** Asked for from "Create care plan" when there is nothing to pick from. */
  @Output() createCarePlan = new EventEmitter<void>();

  readonly collapsedInterventions = COLLAPSED_INTERVENTIONS;
  readonly noteMaxLength = CARE_PLAN_WORK_LIMITS.noteLength;
  readonly titleId = `care-plan-picker-title-${nextPickerId++}`;

  disabled = false;

  private planSections: CarePlanPickerPlan[] = [];
  /** How many problems each plan's goals come from, to group them under headings only when there are several. */
  private problemCounts = new Map<string, number>();

  /** Notes and ticks by plan id; ticks by goal key. A plan is part of the value once one of its boxes is ticked. */
  private notes = new Map<string, string>();
  private ticks = new Map<string, Map<string, CarePlanWorkRequestItem>>();
  private expanded = new Set<string>();
  /** The value last written or emitted, to emit only real changes. */
  private lastValueJson = '[]';
  private syncQueued = false;

  private onChange: (value: CarePlanWorkRequestPlan[]) => void = noop;
  private onTouched: () => void = noop;
  private onValidatorChange: () => void = noop;

  /**
   * What the time form sends for this row: the picked plans, each with its note, in the fixed order the
   * server compares records in. Only goals on screen count: once the sections are ready, a written pick
   * whose goal is not on screen leaves the control, so nothing hidden is sent. `mergeFrozenPicks` puts
   * every stored goal on screen, so that does not happen with what the service loads.
   */
  get value(): CarePlanWorkRequestPlan[] {
    return sortRequestPlans(
      this.planSections
        .map((plan) => ({
          care_plan_id: plan.carePlanId,
          note: (this.notes.get(plan.carePlanId) ?? '').trim(),
          items: plan.goals
            .map((goal) => this.ticks.get(plan.carePlanId)?.get(this.goalKey(goal)))
            .filter((item): item is CarePlanWorkRequestItem => !!item && (item.goal || item.interventions))
            .map((item) => this.cleanItem(item)),
        }))
        .filter((plan) => plan.items.length > 0)
    );
  }

  /** Ticked boxes, as the mockup counts them: a goal with both boxes ticked is two items. */
  get pickedItemCount(): number {
    return this.value.reduce(
      (count, plan) =>
        count + plan.items.reduce((boxes, item) => boxes + (item.goal ? 1 : 0) + (item.interventions ? 1 : 0), 0),
      0
    );
  }

  /** e.g. "3 items picked across 2 of 3 care plans". */
  get summary(): string {
    const items = this.pickedItemCount;
    const total = this.planSections.length;
    return `${items} ${items === 1 ? 'item' : 'items'} picked across ${this.value.length} of ${total} ${
      total === 1 ? 'care plan' : 'care plans'
    }`;
  }

  /** The one message the live region reads out: what to do next, or how much is picked. */
  get statusMessage(): string {
    if (this.status === 'loading') {
      return 'Loading the care plans…';
    }
    if (this.status === 'error') {
      return 'The care plans could not be loaded.';
    }
    if (!this.planSections.length) {
      return this.requirePicks
        ? 'This patient has no active care plan. CCM time needs at least one care plan goal or interventions item.'
        : 'This patient has no active care plan.';
    }
    const errors = this.validate();
    if (errors?.carePlanTooMany) {
      return `Pick at most ${CARE_PLAN_WORK_LIMITS.plans} care plans and ${CARE_PLAN_WORK_LIMITS.goals} goals for one time entry.`;
    }
    if (this.pickedItemCount) {
      return this.summary;
    }
    if (!this.planSections.some((plan) => plan.goals.length > 0)) {
      return this.requirePicks ? 'None of these care plans has an open goal to pick.' : '';
    }
    return this.requirePicks
      ? 'Pick at least one goal or interventions box and write a note for each plan you picked from.'
      : '';
  }

  /** The status line is a warning only when the nurse has something to fix — not while loading, not when all is fine. */
  get statusIsWarning(): boolean {
    if (this.status === 'loading') {
      return false;
    }
    if (this.status === 'error') {
      return true;
    }
    const errors = this.validate();
    return !!errors && !errors.carePlanNoteRequired && !errors.carePlanNoteTooLong;
  }

  /** Offered when the row needs picks and no plan has an open goal to pick, so Save would stay off for good. */
  get showsCreateCarePlan(): boolean {
    return (
      this.status === 'ready' &&
      this.canCreateCarePlan &&
      this.requirePicks &&
      !this.planSections.some((plan) => plan.goals.length > 0)
    );
  }

  get plans(): CarePlanPickerPlan[] {
    return this.planSections;
  }

  @Input()
  set plans(plans: CarePlanPickerPlan[]) {
    this.planSections = plans ?? [];
    this.problemCounts = new Map(
      this.planSections.map((plan) => [plan.carePlanId, new Set(plan.goals.map((goal) => goal.problemId)).size])
    );
    // Picks for goals no longer on screen leave the value, and validity follows the new sections.
    this.syncAfterInputs();
  }

  /** Validity depends on all three inputs; see `syncAfterInputs`. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.requirePicks || changes.status) {
      this.syncAfterInputs();
    }
  }

  writeValue(value: CarePlanWorkRequestPlan[] | null): void {
    this.notes.clear();
    this.ticks.clear();
    this.expanded.clear();
    (value ?? []).forEach((plan) => {
      this.notes.set(plan.care_plan_id, plan.note ?? '');
      this.ticks.set(
        plan.care_plan_id,
        new Map((plan.items ?? []).map((item) => [this.itemKey(item.problem_id, item.goal_id), { ...item }]))
      );
    });
    // What was written, shaped as `value` shapes it — not `value` itself, which only counts goals on
    // screen: when the sections arrive later, an unchanged pick must not read as a change.
    this.lastValueJson = JSON.stringify(
      sortRequestPlans(
        (value ?? [])
          .map((plan) => ({
            care_plan_id: plan.care_plan_id,
            note: (plan.note ?? '').trim(),
            items: (plan.items ?? [])
              .filter((item) => item.goal || item.interventions)
              .map((item) => this.cleanItem(item)),
          }))
          .filter((plan) => plan.items.length > 0)
      )
    );
    // Written once the sections are ready: drop, on the next microtask, any pick whose goal is not on screen.
    if (this.status === 'ready') {
      this.syncAfterInputs();
    }
  }

  registerOnChange(fn: (value: CarePlanWorkRequestPlan[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    if (this.status !== 'ready') {
      return { carePlansNotLoaded: true };
    }
    return validateCarePlanWork(this.planSections, this.value, { required: this.requirePicks });
  }

  isTicked(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox): boolean {
    return this.ticks.get(plan.carePlanId)?.get(this.goalKey(goal))?.[box] === true;
  }

  /**
   * A ticked box can always be unticked. An unticked box can be ticked unless the picker is disabled, the
   * goal has no interventions to pick, or the goal comes from the frozen copy and was not saved with it.
   */
  canChange(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox): boolean {
    if (this.disabled) {
      return false;
    }
    if (this.isTicked(plan, goal, box)) {
      return true;
    }
    if (goal.frozen) {
      return box === 'goal' ? goal.frozenGoal === true : goal.frozenInterventions === true;
    }
    return box === 'goal' || goal.interventions.length > 0;
  }

  toggle(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox, checked: boolean): void {
    if (this.disabled || (checked && !this.canChange(plan, goal, box))) {
      return;
    }
    const planTicks = this.ticks.get(plan.carePlanId) ?? new Map<string, CarePlanWorkRequestItem>();
    const key = this.goalKey(goal);
    const item = planTicks.get(key) ?? {
      problem_id: goal.problemId,
      goal_id: goal.goalId,
      goal: false,
      interventions: false,
    };
    planTicks.set(key, { ...item, [box]: checked });
    this.ticks.set(plan.carePlanId, planTicks);
    this.onTouched();
    this.emitIfChanged();
  }

  hasPicks(plan: CarePlanPickerPlan): boolean {
    return this.value.some((picked) => picked.care_plan_id === plan.carePlanId);
  }

  noteOf(plan: CarePlanPickerPlan): string {
    return this.notes.get(plan.carePlanId) ?? '';
  }

  onNoteInput(plan: CarePlanPickerPlan, event: Event): void {
    this.notes.set(plan.carePlanId, (event.target as HTMLInputElement).value);
    this.emitIfChanged();
  }

  onNoteBlur(): void {
    this.onTouched();
  }

  /** True when this plan is picked from but its note is still blank. */
  isNoteMissing(plan: CarePlanPickerPlan): boolean {
    return this.hasPicks(plan) && !this.noteOf(plan).trim();
  }

  /** Goals are grouped under their problem only when a plan has more than one; most GLB plans have one. */
  showsProblem(plan: CarePlanPickerPlan, index: number): boolean {
    const goals = plan.goals;
    return (
      (this.problemCounts.get(plan.carePlanId) ?? 0) > 1 &&
      (index === 0 || goals[index - 1].problemId !== goals[index].problemId)
    );
  }

  shownInterventions(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): string[] {
    return this.isExpanded(plan, goal) ? goal.interventions : goal.interventions.slice(0, COLLAPSED_INTERVENTIONS);
  }

  isExpanded(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): boolean {
    return this.expanded.has(`${plan.carePlanId}/${this.goalKey(goal)}`);
  }

  toggleExpanded(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): void {
    const key = `${plan.carePlanId}/${this.goalKey(goal)}`;
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  /**
   * The plan's name under its "Care plan:" heading. Plans are often named after the diagnosis plus "Care Plan",
   * which the heading already says, so that ending is dropped instead of reading "Care plan: ... Care Plan".
   */
  planTitle(plan: CarePlanPickerPlan): string {
    return (plan.name ?? '').replace(/\s*care\s*plan\s*$/i, '').trim() || 'Unnamed care plan';
  }

  trackPlan(_index: number, plan: CarePlanPickerPlan): string {
    return plan.carePlanId;
  }

  trackGoal(_index: number, goal: CarePlanPickerGoal): string {
    return `${goal.problemId}/${goal.goalId}`;
  }

  /** Focus goes to the status line first: the button leaves the page as the load starts. */
  onRetry(statusLine: HTMLElement): void {
    statusLine.focus();
    this.retry.emit();
  }

  /** A fresh copy with the request's four fields only. */
  private cleanItem(item: CarePlanWorkRequestItem): CarePlanWorkRequestItem {
    return {
      problem_id: item.problem_id,
      goal_id: item.goal_id,
      goal: item.goal === true,
      interventions: item.interventions === true,
    };
  }

  /**
   * Re-emits and re-validates after an input changed — on the next microtask, out of change detection.
   * Inputs reach the picker while its host is being checked; the time form's Save button, bound to
   * `form.invalid`, is checked before the rows the picker sits in, so a validity flip there would be an
   * ExpressionChanged error in dev and a stale Save button in prod. Queued once, so `plans`, `status` and
   * `requirePicks` changing together cost one check, made once all three are in.
   */
  private syncAfterInputs(): void {
    if (this.syncQueued) {
      return;
    }
    this.syncQueued = true;
    Promise.resolve().then(() => {
      this.syncQueued = false;
      // Sections that are still loading, or failed to, say nothing about which picks are real: dropping
      // the picks of a prefill against an empty list would wipe an edit before the nurse saw it.
      if (this.status === 'ready') {
        this.emitIfChanged();
      }
      this.onValidatorChange();
    });
  }

  private emitIfChanged(): void {
    const value = this.value;
    const json = JSON.stringify(value);
    if (json !== this.lastValueJson) {
      this.lastValueJson = json;
      this.onChange(value);
    }
  }

  private goalKey(goal: CarePlanPickerGoal): string {
    return this.itemKey(goal.problemId, goal.goalId);
  }

  private itemKey(problemId: string, goalId: string): string {
    return `${problemId}/${goalId}`;
  }
}
